"use client"

import { useEffect, useLayoutEffect } from "react"
import { usePathname } from "next/navigation"
import { isDarkShellPath } from "@/lib/dark-shell-routes"

// Flip the attribute before the browser paints the committed tree; fall back to
// useEffect during SSR so this never emits the useLayoutEffect hydration
// warning. Same shape as `components/ui/search-mode-toggle.tsx`.
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect

/**
 * Keeps `html[data-shell]` equal to the CURRENT route.
 *
 * The `<head>` script (`DARK_SHELL_HEAD_SCRIPT`) stamps the attribute once,
 * during head parse, from `location.pathname` — which is the only thing that
 * can colour the pre-paint window on a cold load. But App Router soft
 * navigations never re-parse `<head>`, and nothing else removed the attribute,
 * so it survived the route change.
 *
 * That is the bug reported on 2026-08-04/06: clicking from a dark-hero route
 * (`/`, `/about`, `/insurance`, `/reviews`, `/go/*`) to any light one left
 * `html`/`body` painted `--color-neutral-950`. On a `(default-nav)` page the
 * `<header>` is `sticky top-0` with NO background of its own and a
 * `bg-white/70 backdrop-blur-md` pill, so the body is literally its backdrop —
 * it rendered as a black band with a grey pill above an otherwise light page
 * (`/ch/de/faq`, `/ch/de/membership`, `/ch/de/locations`). Hard-reloading the
 * same URL fixed it, because then the head script ran on the light path and
 * stamped nothing — which is exactly why it read as "1 in 20" and why nobody
 * loading the URLs directly could reproduce it.
 *
 * PR #2156 reasoned the attribute was "safe to leave set for the whole page
 * life" because no `<body>` pixel is visible on the six dark routes once
 * content has painted. That was measured per-route and is still true; what it
 * missed is that a soft nav means the page's life outlives the route.
 *
 * This component does NOT replace the head script — the head script owns first
 * paint, and this owns every route change after it. `<Suspense>` deliberately
 * does not wrap it (see the long comment in `[locale]/layout.tsx`); it renders
 * null, so it costs a hook and nothing else.
 */
export function DarkShellSync() {
  const pathname = usePathname()
  useIsoLayoutEffect(() => {
    const root = document.documentElement
    // `isDarkShellPath` already tolerates the `v/{code}` precompute pair, so
    // the rewritten path `usePathname()` reports during SSR and the public one
    // it reports on the client resolve identically — no `publicPathname()`
    // normalisation needed here.
    if (isDarkShellPath(pathname)) root.setAttribute("data-shell", "dark")
    else root.removeAttribute("data-shell")
  }, [pathname])
  return null
}
